import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { LegalDoc } from '../data/legal';
type LegalModalProps = {
  doc: LegalDoc | null;
  onClose: () => void;
};
const EASE = [0.23, 1, 0.32, 1] as const;
export function LegalModal({
  doc,
  onClose
}: LegalModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!doc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = window.setTimeout(() => closeRef.current?.focus(), 200);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(t);
    };
  }, [doc, onClose]);
  return <AnimatePresence data-id="element-62" data-mp="1cszbp5zpea1h">
      {doc && <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-6" role="dialog" aria-modal="true" aria-labelledby="legal-modal-title" data-id="element-63" data-mp="1cw4xocd1cvsz">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: EASE
      }} onClick={onClose} className="absolute inset-0 bg-black/75 backdrop-blur-sm" data-id="element-64" data-mp="1kow0f6k2mcsy" />

          <motion.div initial={{
        opacity: 0,
        scale: 0.96,
        y: 12
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 12
      }} transition={{
        duration: 0.22,
        ease: EASE
      }} className="relative flex max-h-[88vh] w-full max-w-xl flex-col overflow-hidden rounded-t-2xl border border-white/10 bg-ink-900 shadow-2xl sm:max-h-[80vh] sm:rounded-2xl" data-id="element-65" data-mp="wys5dnky7ldk">
            <div className="flex items-start justify-between gap-4 border-b border-white/10 px-6 py-5" data-id="element-66" data-mp="e794bmahihe6">
              <div data-id="element-67" data-mp="669vptkrrqep">
                <h2 id="legal-modal-title" className="font-display text-xl font-extrabold uppercase leading-tight tracking-tight text-white" data-id="element-68" data-mp="65rra9nye5vp">
                  {doc.title}
                </h2>
                <p className="mt-1 text-xs text-white/40" data-id="element-69" data-mp="ay1fnl9f6xvv">{doc.updated}</p>
              </div>
              <button ref={closeRef} type="button" onClick={onClose} aria-label="Cerrar" className="-mr-2 -mt-1 rounded-lg p-2 text-white/60 outline-none transition-colors duration-150 ease-smooth hover:bg-white/5 hover:text-white focus-visible:ring-2 focus-visible:ring-flame/50" data-id="element-70" data-mp="3jduulvo3s1at">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-71" data-mp="18mbghwxgzfle" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5" data-id="element-72" data-mp="13n8iuxjtkbng">
              <dl className="space-y-5" data-id="element-73" data-mp="13myutfic4eke">
                {doc.sections.map(section => <div key={section.heading} data-id="element-74" data-mp="1b56imac788dh">
                    <dt className="font-display text-sm font-bold uppercase tracking-[0.08em] text-flame" data-id="element-75" data-mp="1g5krtvpjdod3">
                      {section.heading}
                    </dt>
                    <dd className="mt-1.5 text-[0.95rem] leading-relaxed text-white/65" data-id="element-76" data-mp="1285t20de848g">{section.body}</dd>
                  </div>)}
              </dl>
            </div>

            <div className="border-t border-white/10 px-6 py-4" data-id="element-77" data-mp="1ty0wf0y35ug4">
              <button type="button" onClick={onClose} className="w-full rounded-xl border border-white/15 px-5 py-3 font-display text-sm font-bold uppercase tracking-tight text-white outline-none transition-colors duration-150 ease-smooth hover:bg-white/5 focus-visible:ring-2 focus-visible:ring-flame/50" data-id="element-78" data-mp="1yvaj48u3bs09">
                Entendido, cerrar
              </button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}